// src/pages/Clients.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  forwardRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import axiosInstance from "../axiosInstance";
import { notify } from "../ui/notify";


/* =========================================
   Styles communs (alignés sur Devis/Factures)
========================================= */
const BTN_BASE =
  "inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition focus:outline-none focus:ring-2 focus:ring-sky-300";
const btnBlue = `${BTN_BASE} bg-sky-700 hover:bg-sky-600 text-white`;
const btnWhiteOutline = `${BTN_BASE} bg-white border border-gray-300 text-slate-800 hover:bg-gray-50`;
const pillWrap =
  "inline-flex items-center rounded-full border border-slate-300 overflow-hidden";

/* =========================================
   Utils
========================================= */
const normType = (t) =>
  String(t || "").toLowerCase().startsWith("pro") ? "pro" : "particulier";
const normalize = (v = "") =>
  String(v).normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
const fmt2 = (n) =>
  Number(n || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const money = (n) => `${fmt2(n)} €`;
const toNum = (v) =>
  typeof v === "number"
    ? Number.isFinite(v)
      ? v
      : 0
    : Number.isFinite(Number(String(v).replace(/\s/g, "").replace(",", ".")))
      ? Number(String(v).replace(/\s/g, "").replace(",", "."))
      : 0;
const toPct = (v, fb = 20) =>
  v == null
    ? fb
    : Number.isFinite(Number(String(v).replace("%", "")))
      ? Number(String(v).replace("%", ""))
      : fb;
const getTTC = (d = {}) => toNum(d.total_ttc ?? d.montant_ttc ?? d.ttc ?? d.total ?? 0);
const getHT = (d = {}) => {
  const ht = toNum(d.total_ht ?? d.montant_ht ?? d.ht);
  if (ht) return ht;
  const ttc = getTTC(d);
  const tva = toPct(d.tva, 20);
  return ttc / (1 + tva / 100);
};
const isPaid = (f = {}) =>
  /(payee|paye|paid|reglee|regle)/.test(normalize(f.etat ?? f.status ?? "")) ||
  !!f.paid_at;
const dFR = (raw) => {
  if (!raw) return "—";
  const s =
    typeof raw === "string" ? raw.slice(0, 10) : new Date(raw).toISOString().slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};
const fullAddress = (c = {}) =>
  [c.numero_voie, [c.code_postal, c.ville].filter(Boolean).join(" "), c.pays]
    .filter(Boolean)
    .join(" · ");
const parseMeta = (m) => {
  if (!m) return {};
  try {
    return typeof m === "string" ? JSON.parse(m) : m || {};
  } catch {
    return {};
  }
};
/* Multi-tenant */
const getTenantId = () =>
  localStorage.getItem("tenant_id") ||
  localStorage.getItem("account_id") ||
  localStorage.getItem("company_id");
const keyTC = (tenantId, clientId) => `${tenantId || "?"}|${clientId || "?"}`;

/* =========================================
   Hooks utilitaires
========================================= */
function useOutsideClose(refs, onClose) {
  useEffect(() => {
    const onDocClick = (e) => {
      const isInside = refs.some((r) => r.current && r.current.contains(e.target));
      if (!isInside) onClose?.();
    };
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [refs, onClose]);
}

/* =========================================
   Switch HT/TTC (liste + modal)
========================================= */
function TinyHTTTCSwitch({ value = "ttc", onChange }) {
  return (
    <span className="inline-flex rounded-full border border-slate-300 overflow-hidden">
      <button
        type="button"
        onClick={() => onChange?.("ttc")}
        className={`px-2 h-6 text-[11px] leading-none ${value === "ttc" ? "bg-slate-900 text-white" : "bg-white text-slate-600"
          }`}
        title="Montants TTC"
      >
        TTC
      </button>
      <button
        type="button"
        onClick={() => onChange?.("ht")}
        className={`px-2 h-6 text-[11px] leading-none ${value === "ht" ? "bg-slate-900 text-white" : "bg-white text-slate-600"
          }`}
        title="Montants HT"
      >
        HT
      </button>
    </span>
  );
}

/* =========================================
   Toolbar (header identique à Devis/Factures)
========================================= */
function FiltersDropdown({ open, onClose, values, setValues, villes }) {
  const popRef = useRef(null);
  useOutsideClose([popRef], () => open && onClose());
  if (!open) return null;

  return (
    <div
      ref={popRef}
      className="absolute z-[1200] mt-2 w-[340px] rounded-xl border bg-white shadow-xl p-3"
    >
      <div className="space-y-3">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Type</div>
          <select
            className="w-full border rounded px-2 py-1 text-sm"
            value={values.type || ""}
            onChange={(e) => setValues((v) => ({ ...v, type: e.target.value }))}
          >
            <option value="">(Tous)</option>
            <option value="particulier">Particulier</option>
            <option value="pro">Professionnel</option>
          </select>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Ville</div>
          <input
            list="villes-clients"
            className="w-full border rounded px-2 py-1 text-sm"
            placeholder="Tape une ville…"
            value={values.ville || ""}
            onChange={(e) => setValues((v) => ({ ...v, ville: e.target.value }))}
          />
          <datalist id="villes-clients">
            {villes.slice(0, 50).map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Créés entre</div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={values.from || ""}
              onChange={(e) => setValues((v) => ({ ...v, from: e.target.value }))}
            />
            <span className="text-slate-400">→</span>
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm w-full"
              value={values.to || ""}
              onChange={(e) => setValues((v) => ({ ...v, to: e.target.value }))}
            />
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">
            CA cumulé (min / max)
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.01"
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="min"
              value={values.caMin ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, caMin: e.target.value }))}
            />
            <input
              type="number"
              step="0.01"
              className="border rounded px-2 py-1 text-sm w-full"
              placeholder="max"
              value={values.caMax ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, caMax: e.target.value }))}
            />
          </div>
        </div>

        <div className="pt-2 flex items-center justify-end gap-2">
          <button className={btnWhiteOutline} onClick={() => setValues({})}>
            Réinitialiser
          </button>
          <button className={btnBlue} onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function Toolbar({ type, setType, q, setQ, filters, setFilters, villes, onAdd }) {
  const [open, setOpen] = useState(false);
  const leftRef = useRef(null);
  const btnRef = useRef(null);
  useOutsideClose([leftRef, btnRef], () => setOpen(false));

  return (
    <div className="rounded-lg border bg-white px-3 py-2">
      {/* Mobile : 1 colonne / md+ : 3 colonnes */}
      <div className="grid gap-3 items-center md:grid-cols-3">
        {/* GAUCHE : Filtres + Recherche collés */}
        <div className="relative w-full" ref={leftRef}>
          <div className="inline-flex w-full max-w-full items-stretch h-9">
            <button
              ref={btnRef}
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="dropdown-filtres-clients"
              className={
                "px-4 text-sm border border-slate-300 rounded-l-full shrink-0 " +
                (open
                  ? "bg-gray-800 text-white"
                  : "bg-white hover:bg-gray-50 text-slate-700")
              }
            >
              Filtres
            </button>

            {/* Champ de recherche responsive (flex-1) */}
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher un client…"
              aria-label="Rechercher un client"
              className="px-3 text-sm rounded-r-full border border-l-0 border-slate-300 bg-gray-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent flex-1 min-w-0"
              style={{ marginLeft: "-1px" }}
            />
          </div>

          {/* Dropdown filtres sous le bloc filtres+recherche */}
          <div id="dropdown-filtres-clients" className="absolute left-0 top-full">
            <FiltersDropdown
              open={open}
              onClose={() => setOpen(false)}
              values={filters}
              setValues={setFilters}
              villes={villes}
            />
          </div>
        </div>

        {/* CENTRE : Particulier / Professionnel */}
        <div className="flex justify-start md:justify-center">
          <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden">
            <button
              type="button"
              className={
                "px-4 h-9 text-sm " +
                (type === "particulier"
                  ? "bg-gray-300 text-black"
                  : "bg-white text-slate-700")
              }
              onClick={() => setType("particulier")}
              aria-pressed={type === "particulier"}
            >
              Particulier
            </button>
            <button
              type="button"
              className={
                "px-4 h-9 text-sm border-l border-slate-300 " +
                (type === "pro"
                  ? "bg-gray-300 text-black"
                  : "bg-white text-slate-700")
              }
              onClick={() => setType("pro")}
              aria-pressed={type === "pro"}
            >
              Professionnel
            </button>
          </div>
        </div>

        {/* DROITE : Ajouter */}
        <div className="justify-self-end">
          <button
            className="inline-flex items-center justify-center h-9 px-4 rounded-full text-sm font-medium leading-none transition focus:outline-none focus:ring-2 focus:ring-sky-300 bg-sky-700 hover:bg-sky-600 text-white"
            onClick={onAdd}
          >
            Ajouter un nouveau client
          </button>
        </div>
      </div>
    </div>
  );
}



/* =========================================
   Garde non-enregistré
========================================= */
function useUnsavedChanges({ isDirty, onSave }) {
  const [guard, setGuard] = useState({ open: false, resolver: null });
  const ask = useCallback(
    () => new Promise((resolve) => setGuard({ open: true, resolver: resolve })),
    []
  );
  const close = useCallback((res = "cancel") => {
    setGuard((g) => {
      g?.resolver?.(res);
      return { open: false, resolver: null };
    });
  }, []);
  useEffect(() => {
    const before = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [isDirty]);
  const confirmThen = useCallback(
    async (next) => {
      if (!isDirty) return next?.();
      const action = await ask();
      if (action === "leave") return next?.();
      if (action === "save") {
        try {
          await onSave?.();
        } catch { }
        return next?.();
      }
    },
    [isDirty, ask, onSave]
  );
  return { guard, close, confirmThen };
}

function ModernLeaveModal({ onAction }) {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    if (typeof document !== "undefined") setTarget(document.body);
  }, []);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onAction("cancel");
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onAction]);
  if (!target) return null;

  return createPortal(
    <div className="fixed inset-0 z-[2100]">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        onClick={() => onAction("cancel")}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl border border-slate-100">
          <button
            onClick={() => onAction("cancel")}
            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
            aria-label="Fermer"
            title="Fermer"
          >
            ×
          </button>
          <div className="px-8 pt-8 pb-6 text-center">
            <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-sky-50">
              <svg viewBox="0 0 24 24" width="30" height="30" className="text-sky-500">
                <path
                  fill="currentColor"
                  d="M12 12a5 5 0 1 0-5-5 5.006 5.006 0 0 0 5 5zm0 2c-4.336 0-8 2.239-8 5v1h16v-1c0-2.761-3.664-5-8-5z"
                />
                <circle cx="18" cy="6" r="3" fill="currentColor" opacity="0.15" />
              </svg>
            </div>
            <h3 className="text-[18px] font-semibold text-slate-900">
              Des modifications ne sont pas enregistrées
            </h3>
            <p className="mt-2 text-[13px] text-slate-600">
              Si vous quittez maintenant, vos changements seront perdus.
            </p>
          </div>
          <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-center gap-2">
            <button
              className="h-9 px-4 rounded-full text-[13px] border transition border-rose-300 text-rose-600 hover:bg-rose-50"
              onClick={() => onAction("leave")}
            >
              Quitter sans enregistrer
            </button>
            <button
              className="h-9 px-4 rounded-full text-[13px] border transition bg-sky-700 text-white border-sky-700 hover:bg-sky-600 font-medium"
              onClick={() => onAction("save")}
            >
              Enregistrer et quitter
            </button>
          </div>
        </div>
      </div>
    </div>,
    target
  );
}

/* =========================================
   Modal fiche client (création/édition)
========================================= */
function Section({ title, children, subtitle }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white/60 p-4">
      <div className="mb-3">
        <div className="text-[13px] font-semibold text-slate-800">{title}</div>
        {subtitle ? (
          <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>
        ) : null}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
const Label = ({ children }) => (
  <label className="text-[12px] font-medium text-slate-700">{children}</label>
);
const Input = (props) => (
  <input
    {...props}
    className={
      "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[14px] outline-none " +
      "focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 " +
      (props.className || "")
    }
  />
);
const Textarea = (props) => (
  <textarea
    {...props}
    className={
      "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[14px] outline-none " +
      "focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 " +
      (props.className || "")
    }
  />
);

function ClientModal({ open, onClose, initial, onSaved }) {
  const navigate = useNavigate();
  const initialSnapRef = useRef(null);
  const meta0 = parseMeta(initial?.meta);
  const [saving, setSaving] = useState(false);

  const [state, setState] = useState({
    id: initial?.id || null,
    type: normType(initial?.type || "particulier"),
    nom: initial?.nom || "",
    contact: meta0.contact || "",
    fonction: meta0.fonction || "",
    email: initial?.email || "",
    telephone: initial?.telephone || "",
    siret: meta0.siret || "",
    tva: meta0.tva || "",
    iban: meta0.iban || "",
    bic: meta0.bic || "",
    numero_voie: initial?.numero_voie || "",
    complement: meta0.complement || "",
    code_postal: initial?.code_postal || "",
    ville: initial?.ville || "",
    pays: initial?.pays || "France",
    include_addr_in_mail: !!meta0.include_addr_in_mail,
    notes: meta0.notes || "",
  });

  const [docs, setDocs] = useState({ devis: [], factures: [] });
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [priceMode, setPriceMode] = useState("ttc");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const countries = useMemo(
    () => [
      "France",
      "Belgique",
      "Suisse",
      "Luxembourg",
      "Espagne",
      "Italie",
      "Allemagne",
      "Portugal",
      "Pays-Bas",
    ],
    []
  );

  useEffect(() => {
    if (!open) return;
    const m = parseMeta(initial?.meta);
    const next = {
      id: initial?.id || null,
      type: normType(initial?.type || "particulier"),
      nom: initial?.nom || "",
      contact: m.contact || "",
      fonction: m.fonction || "",
      email: initial?.email || "",
      telephone: initial?.telephone || "",
      siret: m.siret || "",
      tva: m.tva || "",
      iban: m.iban || "",
      bic: m.bic || "",
      numero_voie: initial?.numero_voie || "",
      complement: m.complement || "",
      code_postal: initial?.code_postal || "",
      ville: initial?.ville || "",
      pays: initial?.pays || "France",
      include_addr_in_mail: !!m.include_addr_in_mail,
      notes: m.notes || "",
    };
    setState(next);
    initialSnapRef.current = JSON.stringify(next);
  }, [open, initial]);

  const isDirty = useMemo(() => {
    try {
      const cur = JSON.stringify({
        id: state.id || null,
        type: state.type,
        nom: state.nom,
        contact: state.contact,
        fonction: state.fonction,
        email: state.email,
        telephone: state.telephone,
        siret: state.siret,
        tva: state.tva,
        iban: state.iban,
        bic: state.bic,
        numero_voie: state.numero_voie,
        complement: state.complement,
        code_postal: state.code_postal,
        ville: state.ville,
        pays: state.pays,
        include_addr_in_mail: !!state.include_addr_in_mail,
        notes: state.notes,
      });
      return cur !== (initialSnapRef.current || "");
    } catch {
      return false;
    }
  }, [state]);

  const { guard, close, confirmThen } = useUnsavedChanges({
    isDirty,
    onSave: () => save({ silentSuccess: true }),
  });

  useEffect(() => {
    if (!open || !initial?.id) return;
    let ignore = false;
    const tenantId = getTenantId();

    (async () => {
      setLoadingDocs(true);
      try {
        const [rd, rf] = await Promise.all([
          axiosInstance.get(`/devis`, {
            params: { scope: "mine", tenant_id: tenantId, client_id: initial.id },
          }),
          axiosInstance.get(`/factures`, {
            params: { scope: "mine", tenant_id: tenantId, client_id: initial.id },
          }),
        ]);
        const pick = (r) =>
          Array.isArray(r.data) ? r.data : r.data?.data || r.data?.rows || [];

        let dv = pick(rd).map((d) => ({ ...d }));
        let fa = pick(rf).map((f) => ({ ...f }));

        const tId = String(tenantId || "");
        const cId = Number(initial.id);
        dv = dv.filter(
          (d) => String(d.tenant_id || "") === tId && Number(d.client_id) === cId
        );
        fa = fa.filter(
          (f) => String(f.tenant_id || "") === tId && Number(f.client_id) === cId
        );

        if (!ignore) setDocs({ devis: dv, factures: fa });
      } catch {
        if (!ignore) setDocs({ devis: [], factures: [] });
      } finally {
        if (!ignore) setLoadingDocs(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [open, initial?.id]);

  const set = (k) => (e) =>
    setState((s) => ({
      ...s,
      [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const save = async ({ silentSuccess = false } = {}) => {
    setSaving(true);
    try {
      notify.info({
        title: "Enregistrement",
        message: "Sauvegarde du client en cours…",
      });
      const payload = {
        nom: state.nom?.trim() || "",
        email: state.email || "",
        telephone: state.telephone || "",
        type: state.type,
        numero_voie: state.numero_voie || "",
        code_postal: state.code_postal || "",
        ville: state.ville || "",
        pays: state.pays || "",
        meta: {
          contact: state.contact || "",
          fonction: state.fonction || "",
          siret: state.type === "pro" ? state.siret || "" : "",
          tva: state.type === "pro" ? state.tva || "" : "",
          iban: state.type === "pro" ? state.iban || "" : "",
          bic: state.type === "pro" ? state.bic || "" : "",
          complement: state.complement || "",
          include_addr_in_mail: !!state.include_addr_in_mail,
          notes: state.notes || "",
        },
      };
      let res;
      if (state.id) res = await axiosInstance.put(`/clients/${state.id}`, payload);
      else res = await axiosInstance.post(`/clients`, payload);
      const saved = res?.data || {};

      const snap = {
        id: saved.id ?? state.id ?? null,
        ...payload,
        meta: { ...payload.meta },
      };
      initialSnapRef.current = JSON.stringify({
        ...snap,
        include_addr_in_mail: payload.meta.include_addr_in_mail,
      });
      if (!state.id && saved.id) setState((s) => ({ ...s, id: saved.id }));
      notify.success({
        title: "Client à jour",
        message: "Les modifications ont été enregistrées.",
      });
      onSaved?.();
      return saved;
    } catch {
      notify.error({
        title: "Échec",
        message: "Impossible d’enregistrer la fiche client.",
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!state.id) return;
    try {
      await axiosInstance.delete(`/clients/${state.id}`);
      notify.success({ title: "Client supprimé", message: "La fiche a été supprimée." });
      onSaved?.();
      onClose?.();
    } catch {
      notify.error({ title: "Erreur", message: "Suppression impossible." });
    }
  };

  const unifiedDocs = useMemo(() => {
    const rows = [
      ...docs.devis.map((d) => ({
        __type: "devis",
        id: d.id,
        numero: d.numero_devis ? `DEV-${d.numero_devis}` : `DEV-${d.id}`,
        date: d.date_devis || d.created_at,
        client_nom: d.client_nom || state.nom,
        adresse: fullAddress(state),
        statut: d.etat || d.statut,
        _raw: d,
      })),
      ...docs.factures.map((f) => ({
        __type: "facture",
        id: f.id,
        numero: f.numero || f.numero_facture || `FAC-${f.id}`,
        date: (isPaid(f) ? (f.paid_at || f.date) : f.date) || f.created_at,
        client_nom: f.client_nom || state.nom,
        adresse: fullAddress(state),
        statut: f.etat || f.status,
        _raw: f,
      })),
    ];
    return rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [docs, state]);

  const openDoc = (row) => {
    if (row.__type === "devis") return navigate(`/afficher-devis?id=${row.id}`);
    const k = normalize(row._raw?.type || row._raw?.categorie || row._raw?.nature || "");
    const kind = k.includes("acompte")
      ? "acompte"
      : k.includes("partial") || k.includes("partiel")
        ? "partielle"
        : k.includes("final") || k.includes("solde")
          ? "finale"
          : "";
    navigate(
      `/afficher-facture?id=${row.id}${kind ? `&kind=${encodeURIComponent(kind)}` : ""}`
    );
  };

  if (!open) return null;

  const gmapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    [state.numero_voie, state.code_postal, state.ville, state.pays]
      .filter(Boolean)
      .join(" ")
  )}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-sky-800/40" onClick={() => confirmThen(onClose)} />
      <div className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white shadow-xl">
        {/* Header modal */}
        <div className="p-5 border-b flex items-center justify-between">
          <div className="text-xl font-semibold">
            {state.nom || "Nouveau client"}
          </div>
          <div className="flex gap-2">
            {state.id && (
              <>
                <button
                  className="h-9 px-4 rounded-full text-[13px] border transition bg-sky-700 text-white border-sky-700 hover:bg-sky-600"
                  onClick={() =>
                    confirmThen(async () => {
                      if (isDirty) await save({ silentSuccess: true });
                      navigate(
                        `/creer-devis?tenant_id=${getTenantId()}&client_id=${state.id}`
                      );
                    })
                  }
                >
                  Créer un devis
                </button>
                <button
                  className="h-9 px-4 rounded-full text-[13px] border transition bg-sky-700 text-white border-sky-700 hover:bg-sky-600"
                  onClick={() =>
                    confirmThen(async () => {
                      if (isDirty) await save({ silentSuccess: true });
                      navigate(
                        `/creer-facture?tenant_id=${getTenantId()}&client_id=${state.id}`
                      );
                    })
                  }
                >
                  Créer une facture
                </button>
              </>
            )}
            <button
              className="h-9 px-4 rounded-full text-[13px] border transition bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              onClick={() => confirmThen(onClose)}
            >
              Annuler
            </button>
          </div>
        </div>

        {/* Corps modal */}
        <div className="p-6 space-y-5">
          {/* Type client */}
          <div className="flex items-center gap-2">
            <button
              className={`h-9 px-4 rounded-full text-[13px] border transition ${state.type === "particulier"
                ? "bg-sky-700 text-white border-sky-700 hover:bg-sky-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              onClick={() => setState((s) => ({ ...s, type: "particulier" }))}
            >
              Particulier
            </button>
            <button
              className={`h-9 px-4 rounded-full text-[13px] border transition ${state.type === "pro"
                ? "bg-sky-700 text-white border-sky-700 hover:bg-sky-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              onClick={() => setState((s) => ({ ...s, type: "pro" }))}
            >
              Professionnel
            </button>
          </div>

          {/* Infos */}
          <Section title="Informations de l'entreprise">
            <div className="sm:col-span-2">
              <Label>Nom / Société</Label>
              <Input
                value={state.nom}
                onChange={set("nom")}
                placeholder="Nom ou raison sociale"
                required
              />
            </div>
            <div>
              <Label>Mail</Label>
              <Input
                type="email"
                value={state.email}
                onChange={set("email")}
                placeholder="email@domaine.com"
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={state.telephone} onChange={set("telephone")} placeholder="06..." />
            </div>
            <div />
          </Section>

          {state.type === "pro" && (
            <Section title="Informations légales et bancaires">
              <div>
                <Label>SIREN ou SIRET</Label>
                <Input
                  value={state.siret}
                  onChange={set("siret")}
                  placeholder="123 456 789 00012"
                />
              </div>
              <div>
                <Label>N° de TVA intracommunautaire</Label>
                <Input value={state.tva} onChange={set("tva")} placeholder="FRxx xxxxxxxxxxx" />
              </div>
              <div className="sm:col-span-2">
                <Label>IBAN</Label>
                <Input
                  value={state.iban}
                  onChange={set("iban")}
                  placeholder="FR76 xxxx xxxx xxxx xxxx xxxx xxx"
                />
              </div>
              <div className="sm:col-span-1">
                <Label>BIC</Label>
                <Input value={state.bic} onChange={set("bic")} placeholder="XXXXXFRXXXX" />
              </div>
            </Section>
          )}

          {/* Adresse */}
          <div className="rounded-xl border border-slate-200 bg-white/60 p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-[13px] font-semibold text-slate-800">Adresse</div>
              <a
                className="text-[12px] text-sky-700 hover:underline"
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  [state.numero_voie, state.code_postal, state.ville, state.pays]
                    .filter(Boolean)
                    .join(" ")
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                Voir sur Google Maps ↗
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Input
                  value={state.numero_voie}
                  onChange={set("numero_voie")}
                  placeholder="Adresse (ex : 12 rue de Paris)"
                />
              </div>
              <div className="sm:col-span-2">
                <Input
                  value={state.complement}
                  onChange={set("complement")}
                  placeholder="Complément d'adresse"
                />
              </div>
              <div>
                <Input value={state.code_postal} onChange={set("code_postal")} placeholder="Code postal" />
              </div>
              <div>
                <Input value={state.ville} onChange={set("ville")} placeholder="Ville" />
              </div>
              <div className="sm:col-span-2">
                <select
                  value={state.pays}
                  onChange={set("pays")}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-[14px] focus:ring-2 focus:ring-slate-900/10"
                >
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="rounded-xl border border-slate-200 bg-white/60 p-4">
            <div className="text-[13px] font-semibold text-slate-800 mb-2">Notes</div>
            <Textarea
              rows={4}
              value={state.notes}
              onChange={set("notes")}
              placeholder="Historique, préférences, conditions négociées…"
            />
          </div>

          {/* Documents liés */}
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-semibold">Documents du client</div>
            <TinyHTTTCSwitch value={priceMode} onChange={setPriceMode} />
          </div>

          <div className="rounded-xl border overflow-x-auto">
            <table className="min-w-full text-[13px]">
              <thead className="bg-gray-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 w-[12%]">Date</th>
                  <th className="text-left px-3 py-2 w-[12%]">N°</th>
                  <th className="text-left px-3 py-2 w-[10%]">Type</th>
                  <th className="text-left px-3 py-2 w-[22%]">Client</th>
                  <th className="text-left px-3 py-2 w-[26%]">Adresse</th>
                  <th className="text-left px-3 py-2 w-[10%]">Statut</th>
                  <th className="text-right px-3 py-2 w-[8%]">
                    Montant {priceMode.toUpperCase()}
                  </th>
                </tr>
              </thead>
              <tbody className="text-slate-800">
                {loadingDocs ? (
                  <tr>
                    <td className="px-3 py-4 text-center" colSpan={7}>
                      Chargement…
                    </td>
                  </tr>
                ) : unifiedDocs.length === 0 ? (
                  <tr>
                    <td className="px-3 py-4 text-center text-slate-500" colSpan={7}>
                      Aucun document.
                    </td>
                  </tr>
                ) : (
                  unifiedDocs.map((row) => {
                    const s = row._raw;
                    const amount = priceMode === "ttc" ? getTTC(s) : getHT(s);
                    const isQuote = row.__type === "devis";
                    const status = normalize(s?.etat ?? s?.statut ?? s?.status ?? "");
                    let badge = { label: "En cours", class: "bg-amber-100 text-amber-700" };
                    if (isQuote) {
                      if (status.includes("accept"))
                        badge = {
                          label: "Accepté",
                          class: "bg-emerald-100 text-emerald-700",
                        };
                      if (/(abandon|refus|rejet)/.test(status))
                        badge = {
                          label: "Abandonné",
                          class: "bg-gray-100 text-gray-700",
                        };
                      if (status.includes("brouillon"))
                        badge = {
                          label: "Brouillon",
                          class: "bg-gray-100 text-gray-700",
                        };
                    } else {
                      badge = isPaid(s)
                        ? { label: "Payée", class: "bg-emerald-100 text-emerald-700" }
                        : { label: "Envoyée", class: "bg-amber-100 text-amber-700" };
                    }
                    return (
                      <tr
                        key={`${row.__type}-${row.id}`}
                        className="border-t hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDoc(row)}
                        title="Ouvrir le document"
                      >
                        <td className="px-3 py-2">{dFR(row.date)}</td>
                        <td className="px-3 py-2">{row.numero}</td>
                        <td className="px-3 py-2 capitalize">{row.__type}</td>
                        <td className="px-3 py-2">{row.client_nom}</td>
                        <td className="px-3 py-2">{row.adresse}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${badge.class}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">{money(amount)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer modal */}
        <div className="p-5 border-t flex items-center justify-between">
          {state.id ? (
            <button
              className="h-9 px-4 rounded-full text-[13px] border transition border-rose-300 text-rose-600 hover:bg-rose-50 font-medium"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              Supprimer
            </button>
          ) : (
            <span />
          )}

          <div className="flex gap-2">
            <button
              className="h-9 px-4 rounded-full text-[13px] border transition bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              onClick={() => confirmThen(onClose)}
            >
              Annuler
            </button>
            <button
              className="h-9 px-4 rounded-full text-[13px] border transition bg-sky-700 text-white border-sky-700 hover:bg-sky-600 font-medium"
              onClick={() => save()}
              disabled={saving}
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>

      {/* Modaux secondaires */}
      {guard.open && <ModernLeaveModal onAction={(res) => close(res)} />}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 z-[2200] grid place-items-center">
          <div
            className="absolute inset-0 bg-sky-800/40"
            onClick={() => setConfirmDeleteOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl border">
            <div className="p-5 border-b text-[15px] font-semibold">Supprimer le client</div>
            <div className="p-5 text-[13px]">
              ⚠️ Cette action est définitive. Voulez-vous vraiment supprimer ce client ?
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                className="px-3 py-2 rounded border"
                onClick={() => setConfirmDeleteOpen(false)}
              >
                Annuler
              </button>
              <button className="px-3 py-2 rounded bg-rose-600 text-white" onClick={del}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================
   Page : liste des clients (header refait)
========================================= */
export default function Clients() {
  const [clients, setClients] = useState([]);
  const [clientType, setClientType] = useState("particulier");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    type: "",
    ville: "",
    from: "",
    to: "",
    caMin: "",
    caMax: "",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [allDevis, setAllDevis] = useState([]);
  const [allFactures, setAllFactures] = useState([]);

  const [devisMode, setDevisMode] = useState("ttc");
  const [facturesMode, setFacturesMode] = useState("ttc");

  // Raccourci Cmd/Ctrl+K -> focus recherche
  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axiosInstance.get("/clients", { params: { scope: "mine" } });
      setClients(res.data || []);
    } catch (e) {
      console.error("Erreur chargement clients:", e);
    }
  };

  const fetchDocs = async () => {
    try {
      const tenantId = getTenantId();
      const [rd, rf] = await Promise.all([
        axiosInstance.get("/devis", { params: { scope: "mine", tenant_id: tenantId } }),
        axiosInstance.get("/factures", { params: { scope: "mine", tenant_id: tenantId } }),
      ]);
      const pick = (r) =>
        Array.isArray(r.data) ? r.data : r.data?.data || r.data?.rows || [];
      const dv = pick(rd).filter(
        (d) => String(d.tenant_id || "") === String(tenantId || "")
      );
      const fa = pick(rf).filter(
        (f) => String(f.tenant_id || "") === String(tenantId || "")
      );
      setAllDevis(dv);
      setAllFactures(fa);
    } catch (e) {
      console.error("Erreur chargement docs:", e);
      setAllDevis([]);
      setAllFactures([]);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchDocs();
  }, []);

  // agrégation des totaux par client (émis = non brouillon)
  const totalsByClient = useMemo(() => {
    const map = new Map();
    const add = (k, kind, amount) => {
      const cur = map.get(k) || {
        devisHT: 0,
        devisTTC: 0,
        facturesHT: 0,
        facturesTTC: 0,
      };
      map.set(k, { ...cur, [kind]: (cur[kind] || 0) + (amount || 0) });
    };
    const isEmis = (s) => !/brouillon|draft/.test(normalize(s || ""));
    for (const d of allDevis) {
      if (!isEmis(d.etat || d.statut)) continue;
      const k = keyTC(d.tenant_id, d.client_id);
      add(k, "devisHT", getHT(d));
      add(k, "devisTTC", getTTC(d));
    }
    for (const f of allFactures) {
      const k = keyTC(f.tenant_id, f.client_id);
      add(k, "facturesHT", getHT(f));
      add(k, "facturesTTC", getTTC(f));
    }
    return map;
  }, [allDevis, allFactures]);

  // villes pour datalist
  const villes = useMemo(() => {
    const s = new Set();
    clients.forEach((c) => c.ville && s.add(c.ville));
    return Array.from(s).sort((a, b) =>
      String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
    );
  }, [clients]);

  // helpers filtres
  const inDateRange = (iso) => {
    if (!filters.from && !filters.to) return true;
    const t = iso ? new Date(iso).getTime() : NaN;
    if (Number.isNaN(t)) return false;
    if (filters.from && t < new Date(filters.from).getTime()) return false;
    if (filters.to && t > new Date(filters.to).getTime() + 24 * 3600 * 1000 - 1)
      return false;
    return true;
  };
  const inAmountRange = (c) => {
    const min = Number(filters.caMin || NaN);
    const max = Number(filters.caMax || NaN);
    const val = Number(c.ca_total_ttc ?? c.ca_total ?? c.ca ?? 0);
    if (!Number.isFinite(min) && !Number.isFinite(max)) return true;
    if (Number.isFinite(min) && val < min) return false;
    if (Number.isFinite(max) && val > max) return false;
    return true;
  };

  const list = useMemo(() => {
    const q = String(search || "").toLowerCase();
    return (clients || [])
      // bouton segmenté (garde l’onglet visuel)
      .filter((c) => normType(c.type) === clientType)
      // filtre "type" du dropdown (optionnel)
      .filter((c) => !filters.type || normType(c.type) === filters.type)
      // autres filtres
      .filter((c) => !filters.ville || normalize(c.ville) === normalize(filters.ville))
      .filter((c) => inDateRange(c.created_at || c.updated_at))
      .filter((c) => inAmountRange(c))
      // recherche texte
      .filter((c) =>
        [c.nom, c.numero_voie, c.code_postal, c.ville, c.pays, c.email, c.telephone]
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
      .sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
      );
  }, [clients, clientType, filters, search]);

  const getTotalsFor = (c) => {
    const tenantId = getTenantId();
    const k = keyTC(tenantId, c.id);
    const v = totalsByClient.get(k) || {};
    const devis = devisMode === "ttc" ? v.devisTTC || 0 : v.devisHT || 0;
    const fact = facturesMode === "ttc" ? v.facturesTTC || 0 : v.facturesHT || 0;
    return { devis, fact };
  };

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold text-slate-700">Clients</h1>

      <Toolbar
        type={clientType}
        setType={setClientType}
        q={search}
        setQ={setSearch}
        filters={filters}
        setFilters={setFilters}
        villes={villes}
        onAdd={() => {
          setCurrent(null);
          setModalOpen(true);
        }}
      />

      <div className="overflow-auto rounded-lg border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 w-[24%]">Nom</th>
              <th className="px-3 py-2 w-[32%]">Adresse</th>
              <th className="px-3 py-2 w-[18%]">Email</th>
              <th className="px-3 py-2 w-[12%]">Téléphone</th>
              <th className="px-3 py-2 text-right w-[7%]">
                <div className="flex items-center justify-end gap-2">
                  <span className="font-semibold text-slate-700">Devis</span>
                  <TinyHTTTCSwitch value={devisMode} onChange={setDevisMode} />
                </div>
              </th>
              <th className="px-3 py-2 text-right w-[7%]">
                <div className="flex items-center justify-end gap-2">
                  <span className="font-semibold text-slate-700">Factures</span>
                  <TinyHTTTCSwitch value={facturesMode} onChange={setFacturesMode} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">
                  Aucun client.
                </td>
              </tr>
            ) : (
              list.map((c) => {
                const t = getTotalsFor(c);
                return (
                  <tr
                    key={c.id}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setCurrent(c);
                      setModalOpen(true);
                    }}
                    title="Ouvrir la fiche"
                  >
                    <td className="px-3 py-2 font-medium">{c.nom}</td>
                    <td className="px-3 py-2">{fullAddress(c)}</td>
                    <td className="px-3 py-2">{c.email || "—"}</td>
                    <td className="px-3 py-2">{c.telephone || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(t.devis)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(t.fact)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal fiche client */}
      <ClientModal
        open={modalOpen}
        initial={current}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          fetchClients();
          fetchDocs();
        }}
      />
    </div>
  );
}
